import Error from "@/prototype/utils/Error.js";
import ConfirmationArea from "../../ui/ConfirmationArea.js";

import global from "@/prototype/global.js";

const log = global.log.getLogger("f_mtac.storage.space.context_index");
log.setLevel(log.levels.WARN);

import PQueue from "p-queue/dist/index.js";

import {LOCAL_DESKTOP_NUMBER} from "../constants.js";

const CONTEXT_CREATE = 11;
const CONTEXT_DELETE = 12;
const CONTEXT_IGNORE = 13;
// changeset state
const CHANGESET_PENDING = 21;
const CHANGESET_WAITING = 22;
const CHANGESET_PUSHED = 23;

let changeset_prototype = {
    create: function () {
        if (this.method === CONTEXT_DELETE) {
            return;
        }
        this.method = CONTEXT_CREATE;
    },
    delete: function () {
        if (this.method === CONTEXT_CREATE) {
            this.method = CONTEXT_IGNORE;
            return;
        }
        this.method = CONTEXT_DELETE;
    }
};

let Changeset = function (desktop_id) {
    this.method = null;
    this.state = CHANGESET_PENDING;
    this.global_hash = null;
    Object.defineProperty(this, "desktop_id", {
        set: () => {},
        get: () => desktop_id
    });
};
Object.assign(Changeset.prototype, changeset_prototype);

let Store = function (space) {
    var pending = Object();
    var room = undefined;

    Object.defineProperty(this, "room", {
        set: (new_room) => {
            room = new_room;
        },
        get: () => room
    });

    this.init = async (new_room) => {
        pending = Object();
        room = new_room;
    };

    this.deinit = () => {
        pending = Object();
        room = null;
        return Promise.resolve();
    };

    this.commit = ({desktop_id, status, global_hash}) => {
        var changeset = null;
        if (desktop_id === undefined) {
            log.error("a desktop id is necessary to commit a context");
            return;
        }
        if (desktop_id in pending) {
            changeset = pending[desktop_id];
        } else {
            changeset = new Changeset(desktop_id);
            pending[desktop_id] = changeset;
        }
        if (!(status in changeset)) {
            log.error("unhandled status");
            delete pending[desktop_id];
            return;
        }
        changeset[status]();
        if (changeset.method === CONTEXT_IGNORE) {
            delete pending[desktop_id];
            log.error("ignoring changeset", changeset);
            return;
        }
        if (global_hash !== undefined) {
            changeset.global_hash = global_hash;
        }
        return changeset;
    };

    const rx_tx_queue = new PQueue({concurrency: 1});

    const push2 = async () => {
        if (room === null) {
            return;
        }

        let changesets = [],
            conflicts = [];
        for (let desktop_id in pending) {
            let changeset = pending[desktop_id];
            if (changeset.state !== CHANGESET_PENDING) {
                continue;
            }
            changeset.state = CHANGESET_WAITING;
            changesets.push(changeset);
        }

        for (let changeset of changesets) {
            try {
                switch (changeset.method) {
                    case CONTEXT_CREATE:
                        break;
                    case CONTEXT_DELETE:
                        await global.server.mtac_room_desktop_delete({
                            space: room.space,
                            desktop: changeset.desktop_id,
                            global_hash: changeset.global_hash
                        });
                        break;
                }
                changeset.state = CHANGESET_PUSHED;
                delete pending[changeset.desktop_id];
            } catch (error) {
                changeset.state = CHANGESET_PENDING;
                switch (changeset.method) {
                    case CONTEXT_CREATE:
                        log.error(`failed to create new context ${changeset.desktop_id} on server, will continue trying`);
                        break;
                    case CONTEXT_DELETE:
                        log.error(`failed to delete context ${changeset.desktop_id} on server, will continue trying`);
                        break;
                }
                switch (error.status) {
                    case Error.status.netdown:
                    case Error.status.hostdown:
                        break;
                    case Error.status.conflict:
                        delete pending[changeset.desktop_id];
                        conflicts.push(changeset);
                        continue;
                    default:
                        log.error("failed to handle context error, will try again next time", error);
                        continue;
                }
            }
        }

        if (conflicts.length > 0) {
            throw new Error.build.conflict({
                message: "context update conflict",
                data: {conflicts}
            });
        }
    };

    this.push = () => {
        let sz = rx_tx_queue.size;
        log.debug("ContextIndex.push ", {sz, rx_tx_queue});

        if (rx_tx_queue.sizeBy({priority: 1}) > 1) {
            log.debug("ContextIndex.push return");
            return;
        }

        rx_tx_queue.add(() => push2(), {priority: 1});
    };

    // @brief check room status on server side
    // @return 0 -> no room in the context
    //         http status code (200, 400, 401, 403, 409, ...)
    this.check_room = async () => {
        log.debug("ContextIndex.check_room");
        if (room === null) {
            log.debug("ContextIndex.check_room return 0");
            return 0;
        }
        try {
            var response = await global.server.mtac_room_check(room);
            log.debug(`ContextIndex.check_room return ${response.status}`);
            return response.status;
        } catch (error) {
            log.error("ContextIndex.check_room error", {error});
            log.debug(`ContextIndex.check_room return ${error.status}`);
            return error.status;
        }
    };

    const pull2 = async () => {
        if (room === null) {
            return;
        }
        log.debug("ContextIndex.pull2 in", {room});
        try {
            let response = await global.server.mtac_room_desktop_list(room);
            var {name, list} = {name: response.data.name, list: response.data.list};
        } catch (error) {
            switch (error.status) {
                case Error.status.netdown:
                case Error.status.hostdown:
                    //log.debug("pull2 OUT");
                    return;
                case Error.status.auth:
                    log.debug("ContextIndex.pull2  error authentication, uncomment ?????");
                    break;
                default:
                    throw error;
            }
        }

        let request_conflict_confirmation = undefined;
        let request_conflict = async () => {
            let confirmation = await ConfirmationArea.interact({
                title: global.internationalization.text("collaboration.Conflict"),
                tip1: global.internationalization.text("collaboration.Local-crisis-cannot-be-synchronized"),
                tip2: global.internationalization.text("collaboration.Accept-server-crisis"),
                accept: true,
                backdrop: "static"
            });
            if (!confirmation) {
                await global.storage.unshare({});
            }
            return confirmation;
        };

        if (name !== undefined) {
            space.name = name;
        }
        if (!list) {
            return;
        }
        if (list.length < LOCAL_DESKTOP_NUMBER) {
            throw "list is not providing enough desktops";
        }
        for (let desktop_index = 0; desktop_index < LOCAL_DESKTOP_NUMBER; desktop_index++) {
            if (list[desktop_index] === null) {
                await space.delete_context_safe({desktop_index});
                continue;
            }
            let {background_image} = list[desktop_index]; // background_image stored in server = map media id
            let map_media_id = background_image;
            if (map_media_id === null) {
                await space.delete_context_safe({desktop_index});
                continue;
            }
            let context = space.context[desktop_index];
            if (context === null) {
                await space.create_context({desktop_index});
                await space.context[desktop_index].pull();
                continue;
            }
            let context_map_media = await context.map_media_id;
            if (context_map_media !== null && `media-${map_media_id}` !== context_map_media) {
                log.warn("background image is different, re-synchronizing", map_media_id, "!=", context_map_media);
                if (request_conflict_confirmation === undefined) {
                    request_conflict_confirmation = await request_conflict();
                }
                if (!request_conflict_confirmation) {
                    return;
                }
                await space.reset_context({desktop_index});
                await space.context[desktop_index].pull();
            }
        }
        log.debug("ContextIndex.pull2 out");
    };

    this.pull = async () => {
        log.debug( "ContextIndex.pull");
        if (rx_tx_queue.sizeBy({priority: 0}) > 1) {
            return;
        }
        return rx_tx_queue.add(() => pull2(), {priority: 0});
    };
};

export default Store;
